import { ProjectProgressBar } from "components/ProjectTimeline";
import React from "react";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

interface Props {
    budget: number;
    currency: string;
    invoicedCosts: number;
}

export const getBudgetProgress = (budget: number, invoicedCosts: number) => {
    if (budget <= 0) {
        return invoicedCosts > 0 ? 100 : 0;
    }
    return Math.max(0, (invoicedCosts / budget) * 100);
};

const ProjectBudgetProgress: React.FC<Props> = ({ budget, currency, invoicedCosts }) => {
    const { hasProvider, intlLocale, t } = useOfficeTranslation();
    const progress = getBudgetProgress(budget, invoicedCosts);
    const remaining = budget - invoicedCosts;
    const overBudget = remaining < 0;
    const money = new Intl.NumberFormat(hasProvider ? intlLocale : "nl-NL", { style: "currency", currency });
    const progressLabel = budget > 0
        ? t("projects.projectBudgetProgress.percentagePercentageOfTheBudgetUsed", {percentage: Math.round(progress)})
        : invoicedCosts > 0
            ? t("projects.projectBudgetProgress.costsWithoutAConfiguredBudget")
            : t("projects.projectBudgetProgress.noBudgetUsedYet");

    return <div className="pt-3">
        <div className="d-flex justify-content-between gap-3 mb-3">
            <div>
                <span className="text-muted d-block">{t("common.budget")}</span>
                <strong>{money.format(budget)}</strong>
            </div>
            <div className="text-end">
                <span className="text-muted d-block">{t("projects.projectBudgetProgress.invoicedCosts")}</span>
                <strong>{money.format(invoicedCosts)}</strong>
            </div>
        </div>
        <ProjectProgressBar
            ariaLabel={progressLabel}
            progress={progress}
            tone={overBudget ? "danger" : "default"}
        />
        <div className="d-flex justify-content-between gap-3 mt-3 small">
            <span className="text-muted">{progressLabel}</span>
            <strong className={overBudget ? "text-danger text-end" : "text-success text-end"}>
                {overBudget
                    ? `${money.format(Math.abs(remaining))} ${t("projects.projectBudgetProgress.overBudget")}`
                    : `${money.format(remaining)} ${t("common.remaining")}`}
            </strong>
        </div>
    </div>;
};

export default ProjectBudgetProgress;
